import React from 'react';

import { formatFigure, formatToFixedMoneyWithLocale } from 'Helpers/formatService';

const DASH = '—';

const movementColour = (movement) => {
    if (movement > 0) {
        return 'bc-mv-pos';
    }

    return movement < 0 ? 'bc-mv-neg' : '';
};

// A deactivated pot has no balance; a pot standing at zero still holds one, so it shows
// as a figure rather than the dash every other zero reads as.
const balanceCell = (balance) => {
    if (balance === null || balance === undefined) {
        return <span className="bc-dim">{DASH}</span>;
    }

    const n = Number(balance);

    return n === 0 ? formatToFixedMoneyWithLocale(0, 2) : formatFigure(n, formatToFixedMoneyWithLocale, { precision: 2 });
};

/**
 * The movement figure arrives already signed from the server, computed from the
 * row's old and new balance. Never re-derive the sign from the formatted string —
 * that is what the old reconciliation table did, and it dropped the sign.
 */
export default function MovementsTable({ rows, closingBalance, showContingencyColumn = true }) {
    return (
        <div className="inbud-table_wrapper bc-table-wrapper">
            <table className="inbud-table bc-table">
                <thead>
                    <tr>
                        <th scope="col">{$t('Date')}</th>
                        {showContingencyColumn && <th scope="col">{$t('Contingency')}</th>}
                        <th scope="col">{$t('Type')}</th>
                        <th scope="col">{$t('Reference')}</th>
                        <th scope="col">{$t('Description')}</th>
                        <th scope="col" className="bc-num">
                            {$t('Movement')}
                        </th>
                        <th scope="col" className="bc-num">
                            {$t('Balance')}
                        </th>
                        <th scope="col">{$t('User')}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={showContingencyColumn ? 8 : 7} className="tw-text-center tw-text-charcoal-50">
                                {$t('No movements for these filters.')}
                            </td>
                        </tr>
                    )}

                    {rows.map((row) => {
                        const movement = Number(row.movement);

                        return (
                            <tr key={row.id}>
                                <td className="tw-tabular-nums">{row.date}</td>
                                {showContingencyColumn && <td>{row.contingency}</td>}
                                <td>
                                    {row.type_url ? (
                                        <a className="bc-chip bc-chip--grey hover:tw-underline" href={row.type_url}>
                                            {row.type}
                                        </a>
                                    ) : (
                                        <span className={`bc-chip ${row.automatic ? 'bc-chip--grey' : 'bc-chip--blue'}`}>{row.type}</span>
                                    )}
                                </td>
                                <td>
                                    {!row.reference ? (
                                        <span className="bc-dim">{DASH}</span>
                                    ) : row.reference.url ? (
                                        <a className="bc-link" href={row.reference.url}>
                                            {row.reference.label}
                                        </a>
                                    ) : (
                                        row.reference.label
                                    )}
                                </td>
                                <td>{row.description || <span className="bc-dim">{DASH}</span>}</td>
                                {/* A deactivation moves no money but still earns a ledger row,
                                    so exactly zero reads neutral — neither a gain nor a loss. */}
                                <td className={`bc-num ${movementColour(movement)}`}>
                                    {movement > 0 && '+'}
                                    {formatFigure(movement, formatToFixedMoneyWithLocale, { precision: 2 })}
                                </td>
                                <td className="bc-num">{balanceCell(row.balance)}</td>
                                <td>{row.user}</td>
                            </tr>
                        );
                    })}

                    {closingBalance !== undefined && (
                        <tr className="bc-row--total">
                            <td colSpan={showContingencyColumn ? 6 : 5}>{$t('Current balance — reconciles to Contingencies & Approvals tab')}</td>
                            <td className="bc-num">{balanceCell(closingBalance)}</td>
                            <td />
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}
